import { useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface UsePageVisibilityOptions {
  onVisible?: () => void;
  onHidden?: () => void;
  checkSessionOnVisible?: boolean;
}

export function usePageVisibility(options: UsePageVisibilityOptions = {}) {
  const { 
    onVisible, 
    onHidden, 
    checkSessionOnVisible = true 
  } = options;
  
  const { refreshSession, user } = useAuth();
  
  const handleVisibilityChange = useCallback(async () => {
    if (document.hidden) {
      // Page is hidden (tab switched or minimized)
      onHidden?.();
    } else {
      // Page is visible again
      onVisible?.();
      
      // Check session validity when tab becomes visible
      if (checkSessionOnVisible && user) {
        try {
          // Check if session is still valid
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error || !session) {
            toast.error('Your session has expired. Please sign in again.', {
              duration: 5000,
              id: 'session-check'
            });
            
            // Redirect to sign in
            window.location.href = '/signin?redirect=' + encodeURIComponent(window.location.pathname);
          } else {
            // Session is valid, but let's refresh it proactively if it's close to expiry
            const expiresAt = session.expires_at;
            if (expiresAt) {
              const expiresIn = expiresAt * 1000 - Date.now();
              const shouldRefresh = expiresIn < 10 * 60 * 1000; // Less than 10 minutes
              
              if (shouldRefresh) {
                try {
                  await refreshSession();
                  console.log('Session refreshed after tab reactivation');
                } catch (error) {
                  console.error('Failed to refresh session:', error);
                }
              }
            }
          }
        } catch (error) {
          console.error('Error checking session on visibility change:', error);
        }
      }
    }
  }, [onVisible, onHidden, checkSessionOnVisible, user, refreshSession]);
  
  useEffect(() => {
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also listen for focus events as a backup
    const handleFocus = () => {
      if (!document.hidden) {
        handleVisibilityChange();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [handleVisibilityChange]);
  
  return {
    isVisible: !document.hidden
  };
}

// Hook to use in components that need session freshness
export function useSessionFreshness() {
  usePageVisibility({
    onVisible: () => {
      console.log('Page became visible, checking session...');
    },
    checkSessionOnVisible: true
  });
}